import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import HomeIcon from '@mui/icons-material/Home'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useGetAuthoringComponentQuery, useGetManifestQuery } from '../../api/authoring'
import type { Document, Manifest, ManifestType } from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { useIntent, shown } from '../../api/useIntent'
import { intentTitle, proseOf, structuralIntents, structuralPayload } from '../../model/manifest'
import { pageFields } from '../../model/page'
import { listedTypes, members, recordAddress } from '../../model/records'
import { routes } from '../../model/routes'
import {
  canonicalHost,
  flattenRoutes,
  menusInUse,
  resolveRouting,
  routeMenus,
  websiteFields,
  PRIMARY_MENU,
  SITE_TYPE,
  type ResolvedRoute,
} from '../../model/website'
import { DragHandle } from '../../ui/DragHandle'
import { Loading, QueryError } from '../../ui/QueryState'
import { RowMenu } from '../../ui/RowMenu'
import { UndoButton } from '../../ui/UndoButton'
import { moved, useReorder, type Reorder } from '../../ui/reorder'
import { AddPageDialog } from './AddPageDialog'
import { PageSettings, type PageSettingsValues } from './PageSettings'
import { PublicationStatus } from '../site/PublicationStatus'

/**
 * A site, as its owner meets it: the pages, in the order they appear.
 *
 * **This is the same document the site editor holds, drawn narrower.**
 * It reads `resolveRouting` — the identical function, not a second
 * reading of the routing tree — and sends the identical intents. What it
 * leaves out is everything an owner has no stake in: link ids, pin
 * state, domains, slot defaults, the 404 placement, and the derived form
 * over every intent the type happens to declare. Those are the site's
 * Routing face, at `…/routing`, which is one link away and is the same
 * tree.
 *
 * That constraint is the whole design. Two screens over one model is a
 * register change; two models is a rewrite, and the second one drifts.
 *
 * **The pages are a table, and that is a correctness claim rather than a
 * taste one.** Each row used to be a flex row: a name at the left edge,
 * a spacer, and then whatever chips that particular route happened to
 * warrant pinned to the right. Every fact about a page therefore sat at
 * a different horizontal position on every row — a page's address under
 * its title, its menus somewhere in the middle-right depending on how
 * long the title was — so the only way to answer "which of these is not
 * in a menu" was to read all eight rows end to end. Columns answer it by
 * being scanned down, which is the question an owner actually asks of a
 * list of pages, and on a wide screen it closes the metre of dead space
 * the spacer opened in the middle of each row.
 *
 * The cost accepted: a table is rigid about its cells, so a route whose
 * facts do not fit these three columns has nowhere to put them. That is
 * the point. A fourth kind of fact is now a conversation about a column
 * rather than one more chip appended to one row.
 *
 * **And a column earns its width by being read, not by being plausible.**
 * This drew a Status column first, because the comp has one and because
 * the two odd things a route can be — a heading that opens nothing, a
 * page that has gone — are status-shaped. But the platform exposes no
 * per-page publication state, so on a real site the column was empty
 * down its entire length while holding a seventh of the table. An empty
 * column with a name on it is worse than an absent one: it is a promise
 * to a reader scanning for the thing it is named after. The two facts it
 * held moved to Menu, which answers the same question they answer —
 * how does anyone reach this page — and the width went back to the
 * page's name and its address.
 *
 * **A row is selectable, and the selection is edited beside the table.**
 * This was the last list in the client that could only be read. Changing
 * a page's title from here meant opening the page, changing face to
 * Settings, typing eight characters, saving, and coming back — four
 * navigations around one field, for the single most common edit anybody
 * makes to a site. The panel is the same select-then-edit idiom the page
 * editor, the pattern designer and the type designer all use now, and
 * the reason it arrives last is that this screen had nothing selectable
 * on it until the table gave every row one identity.
 *
 * **The form in the panel is `PageSettings`, unchanged**, which is the
 * whole reason the panel is affordable. That form already knows the one
 * hard thing about editing a page: a page's title is the Page's and its
 * slug, menu name and menu membership are the Website's, and each
 * changed fact goes as its own intent to whichever component owns it. A
 * second form over the same two documents would be a second place for
 * that split to be got wrong.
 *
 * **The name stays a link and the row is the selection**, which is the
 * one thing about this pattern worth stating: clicking a page's name
 * still opens the page, because that is what a name has always done
 * here and a list that stopped opening things to gain an inspector would
 * have traded away more than it gained. Selection is the rest of the
 * row, and the panel carries an explicit way in as well.
 */
export function ContentSite() {
  const { tenant = '', site = '' } = useParams()
  const manifest = useGetManifestQuery({ tenant })
  const component = useGetAuthoringComponentQuery({ tenant, id: site })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)
  const [adding, setAdding] = useState<{ under: string } | null>(null)

  // Selection is `?sel=`, the same parameter and the same rule as every
  // other selection in this client: the path is where the reader is
  // standing and the query is what they have open. It is `replace` for
  // the same reason the page editor's is — clicking down a list of eight
  // pages should not put eight entries in the history, and Back should
  // leave the screen rather than walk the selection backwards.
  const [params, setParams] = useSearchParams()
  const selected = params.get('sel') ?? ''
  const select = useCallback(
    (key: string) => {
      const next = new URLSearchParams(params)
      if (!key) next.delete('sel')
      else next.set('sel', key)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const typeName = component.data?.type ?? SITE_TYPE
  const structural = useMemo(
    () => new Map(structuralIntents(manifest.data, typeName).map((intent) => [intent.name, intent])),
    [manifest.data, typeName],
  )

  const act = useCallback(
    (name: string, supplied: Parameters<typeof structuralPayload>[1]) => {
      const intent = structural.get(name)
      if (!intent) return
      send(site, name, structuralPayload(intent, supplied)).catch(shown)
    },
    [send, site, structural],
  )

  const reorder = useReorder(
    (under, order) => act('website.routes_reordered', { link: under || undefined, order }),
    !sending,
  )

  if (manifest.isLoading || component.isLoading) return <Loading label="Reading the site…" />
  if (component.error) return <QueryError error={component.error} title="Could not open this site" />
  if (!component.data) return null

  const document = component.data
  const fields = websiteFields(document)
  const siteRoutes = flattenRoutes(resolveRouting(document))
  const host = canonicalHost(fields)
  const says = (intent: string) => intentTitle(manifest.data, typeName, intent)
  const selectedRoute = siteRoutes.find((route) => selectionKey(route) === selected)
  const selectedParent = selectedRoute?.under
    ? siteRoutes.find((route) => route.route.page === selectedRoute.under)
    : undefined
  // A generated route is one page per record, so it is shown as the list
  // those records are kept in rather than as a page of its own.
  const listedAs = (route: ResolvedRoute): ManifestType | undefined => {
    const source = (document.references ?? []).find(
      (reference) => reference.name === 'routeSource' && reference.edgeId === route.route.source,
    )
    return listedTypes(manifest.data).find((declared) => declared.collection === source?.id)
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
          <Typography variant="h1">{fields.title || fields.name || document.id}</Typography>
          {host && (
            <MuiLink href={`https://${host}`} target="_blank" rel="noreferrer" variant="body2">
              {host} <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
            </MuiLink>
          )}
        </Stack>
        <PublicationStatus tenant={tenant} site={site} />
        <UndoButton tenant={tenant} document={document} manifest={manifest.data} disabled={sending} />
        <Button variant="contained" startIcon={<AddIcon />} disabled={sending} onClick={() => setAdding({ under: '' })}>
          {says('website.page_added')}
        </Button>
      </Stack>

      {sendError && <Alert severity="error">{problemMessage(sendError, 'That change could not be made.')}</Alert>}

      {/* The table and its inspector, in the two-column shape the page
          editor and the designers use. The Lists table keeps the left
          column rather than running under both, because it is a second
          list of the same kind and a table stretched under a panel that
          has nothing to say about it reads as one wide thing with a
          notch cut out of it.

          The panel spans both rows rather than sitting in the first one.
          Sharing a row with the pages table made that row as tall as
          whichever was taller, so a selected page — a form of seven
          controls — pushed Lists a third of a screen down and left a gap
          under the pages with nothing in it. Spanning is also what the
          sticky panel wants: it can follow the reader down past the
          second table instead of stopping at the first one's floor.

          The tables scroll sideways inside their own paper rather than
          shedding a column. A panel takes 400px off a 1280px window and
          the row actions were the first thing over the edge — clipped,
          not hidden, which is the failure mode where a reader cannot
          even tell something is missing. A column that has to be scrolled
          to is worse than one that does not and better than one that is
          gone, and it costs nothing on the width this screen is mostly
          read at.

          Below `md` the grid is one column and the order is table,
          panel, lists: the panel belongs *under what was clicked* on a
          narrow screen, not under everything on the screen. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(320px, 400px)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
      <Paper sx={{ overflowX: 'auto' }}>
        <Stack sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h3">Pages</Typography>
          <Typography variant="caption" color="text.secondary">
            In the order they appear in the menu. Choose one to edit it, or drag it to move it.
          </Typography>
        </Stack>

        {siteRoutes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }}>
            There are no pages yet, so there is nothing on this site to visit.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%' }}>Page</TableCell>
                <TableCell sx={{ width: '36%' }}>Address</TableCell>
                <TableCell sx={{ width: '18%' }}>Menu</TableCell>
                {/* The actions column is headed by nothing on purpose: a
                    word over a menu button would be the only column
                    label that named a control rather than a fact. */}
                <TableCell align="right" sx={{ width: '6%' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {siteRoutes.map((route, index) => (
                <PageRow
                  key={`${route.urlPath}-${route.route.page ?? index}`}
                  route={route}
                  tenant={tenant}
                  site={site}
                  listedAs={listedAs}
                  says={says}
                  reorder={reorder}
                  onAddUnder={(under) => setAdding({ under })}
                  onAct={act}
                  selected={selectionKey(route) === selected}
                  onSelect={select}
                  busy={sending}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

        <Box sx={{ gridColumn: { md: '2' }, gridRow: { md: '1 / span 2' }, alignSelf: 'stretch' }}>
          <PageInspector
            tenant={tenant}
            site={site}
            website={document}
            manifest={manifest.data}
            selected={selected}
            route={selectedRoute}
            parent={selectedParent}
            listedAs={listedAs}
            send={send}
            busy={sending}
            onClear={() => select('')}
          />
        </Box>

        <Box sx={{ gridColumn: { md: '1' }, gridRow: { md: '2' } }}>
          <Lists tenant={tenant} site={site} manifest={manifest.data} website={document} />
        </Box>
      </Box>

      <AddPageDialog
        tenant={tenant}
        site={site}
        open={adding !== null}
        under={adding?.under ?? ''}
        document={document}
        manifest={manifest.data}
        onClose={() => setAdding(null)}
      />
    </Stack>
  )
}

/**
 * What `?sel=` holds for a row.
 *
 * **A link id where the route has one, and the URL path where it does
 * not.** The routing tree gives two of the three kinds of row a real
 * identity — a page route is keyed by the link id of the placement
 * serving it, a generated route by its own node id — and those are what
 * a selection should be made of, because they survive the edit the panel
 * exists to make. The panel changes a page's slug; if the selection were
 * the address, saving a new address would deselect the row the author
 * was editing and the form would vanish under their hands.
 *
 * A menu heading has neither: it serves no page, so it has no placement,
 * and it is not generated, so it has no node id. `resolveRouting` gives
 * it an empty key, which cannot address anything. Its URL path is all it
 * has, and it is enough here for the reason that makes the fallback safe
 * rather than merely convenient: nothing the panel offers for a heading
 * edits it, so the value cannot go stale while it is being used. The two
 * namespaces cannot collide either — a link id is a link id and a URL
 * path always begins with `/`.
 */
const selectionKey = (route: ResolvedRoute): string => route.key || route.urlPath

/**
 * The row's click handler, which a link inside the row is exempt from.
 *
 * **A click on a name is a navigation and nothing else.** Without the
 * exemption it is both, and the two fight: selecting writes `?sel=` with
 * `replace`, and a replace landing in the same tick as the router's push
 * throws the push away — so clicking "Restoration" highlighted the row
 * and stayed exactly where it was. That is the worst possible outcome,
 * because the reader's click did visibly *something* and it was not the
 * thing they asked for.
 *
 * Asking the DOM whether the click began inside an anchor, rather than
 * stopping propagation on each link, keeps the rule in one place and
 * makes it true of links this row grows later. The row menu is
 * deliberately not exempt: opening the actions for a page is a fine
 * reason to have that page in the panel, and it does not navigate.
 */
const selectsRow = (onSelect: () => void) => (event: MouseEvent<HTMLElement>) => {
  if ((event.target as HTMLElement).closest('a')) return
  onSelect()
}

/**
 * How deep a row sits, as padding on its first cell.
 *
 * Nesting in a table is indentation and nothing else: a child route is
 * not a sub-table, because the moment it is one its address and its menu
 * stop lining up with its parent's and the columns have been given away
 * to express a relationship a 24px step already expresses. The step is
 * per level and the padding is the cell's own, so the drag handle, the
 * arrow on a generated row and the name all move together.
 */
const indented = (depth: number) => ({ pl: 2 + depth * 3 })

/** The line drawn where a dragged row would land. */
function landingEdge(landing: 'above' | 'below' | undefined) {
  // On a collapsed table the row itself is not reliably paintable, so
  // the line goes on the cells. It is the same 2px primary edge the
  // Stack rows drew, one element down.
  if (!landing) return undefined
  return {
    '& > td': landing === 'above'
      ? { borderTop: 2, borderTopColor: 'primary.main' }
      : { borderBottom: 2, borderBottomColor: 'primary.main' },
  }
}

/** The mono address cell, which is the same in both kinds of row. */
function AddressCell({ path }: { path: string }) {
  return (
    <TableCell sx={{ fontFamily: 'ui-monospace, monospace', color: 'text.secondary', fontSize: '0.75rem' }}>
      {path}
    </TableCell>
  )
}

function PageRow({
  route,
  tenant,
  site,
  listedAs,
  says,
  reorder,
  onAddUnder,
  onAct,
  selected,
  onSelect,
  busy,
}: {
  route: ResolvedRoute
  tenant: string
  site: string
  listedAs: (route: ResolvedRoute) => ManifestType | undefined
  says: (intent: string) => string
  reorder: Reorder<undefined>
  onAddUnder: (under: string) => void
  onAct: (name: string, supplied: Parameters<typeof structuralPayload>[1]) => void
  selected: boolean
  onSelect: (key: string) => void
  busy: boolean
}) {
  if (route.generated)
    return (
      <GeneratedRow
        route={route}
        tenant={tenant}
        site={site}
        listed={listedAs(route)}
        selected={selected}
        onSelect={onSelect}
      />
    )
  const page = route.route.page ?? ''
  // A route serving no page is a heading in the menu. It is a real thing
  // an owner made, so it is drawn — with the one sentence that explains
  // why it does not open.
  const grouping = !page
  const movable = !grouping && route.siblings.length > 1
  const position = page ? route.siblings.indexOf(page) : -1
  const drag = movable ? reorder.rowProps(route.under, page, route.siblings) : {}
  const landing = movable ? reorder.landingOn(route.under, page) : undefined
  const menus = routeMenus(route.route)

  const step = (direction: -1 | 1) => {
    const target = route.siblings[position + direction]
    if (target === undefined) return
    const order = moved(route.siblings, page, target)
    if (order) onAct('website.routes_reordered', { link: route.under || undefined, order })
  }

  return (
    <TableRow
      {...drag}
      hover
      selected={selected}
      onClick={selectsRow(() => onSelect(selectionKey(route)))}
      sx={{
        cursor: 'pointer',
        opacity: movable && reorder.isHeld(route.under, page) ? 0.4 : 1,
        ...landingEdge(landing),
      }}
    >
      <TableCell sx={indented(route.depth)}>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* The grip is kept in the flow even where there is nothing to
              drag, because a heading whose name started 20px left of
              every other name would read as a different kind of row than
              it is. */}
          {grouping ? (
            <Box sx={{ width: 20, flexShrink: 0 }} />
          ) : (
            <DragHandle label={says('website.routes_reordered')} disabled={!movable || busy} />
          )}
          {route.targetId ? (
            <MuiLink component={RouterLink} to={routes.page(tenant, site, route.targetId)} sx={{ fontWeight: 500 }}>
              {route.route.title || route.route.slug || 'Untitled page'}
            </MuiLink>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {route.route.title || route.route.slug || 'Untitled page'}
            </Typography>
          )}
          {/* The home page is a property of the page, not of its menus,
              so it stays beside the name where the eye is already
              looking rather than in a column of its own. */}
          {route.isHome && <Chip color="primary" variant="outlined" icon={<HomeIcon />} label="Home page" />}
        </Stack>
      </TableCell>

      <AddressCell path={route.urlPath} />

      {/* How anyone reaches this page, in words. The site editor shows
          the same fact as an icon and a chip carrying a link id, which
          is the right register there and the wrong one here.
          The two odd routes answer the same question and so share the
          column: a heading is reachable *only* from the menu and opens
          nothing, and a dangling route is in a menu that leads nowhere —
          which is why that one is drawn as the error it is. */}
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {menus.length === 0 ? (
            <Chip variant="outlined" icon={<VisibilityOffIcon />} label="Not in a menu" />
          ) : (
            menus.map((menu) => (
              <Chip key={menu} variant={menu === PRIMARY_MENU ? 'filled' : 'outlined'} label={menu} />
            ))
          )}
          {grouping && <Chip variant="outlined" label="Menu heading" />}
          {route.dangling && <Chip color="error" label="This page is missing" />}
        </Stack>
      </TableCell>

      <TableCell align="right">
        {!grouping && (
          <RowMenu
            disabled={busy}
            actions={[
              {
                label: 'Move up',
                icon: <ArrowUpwardIcon fontSize="small" />,
                disabled: position <= 0,
                onSelect: () => step(-1),
              },
              {
                label: 'Move down',
                icon: <ArrowDownwardIcon fontSize="small" />,
                disabled: position < 0 || position >= route.siblings.length - 1,
                onSelect: () => step(1),
              },
              {
                label: says('website.home_set'),
                icon: <HomeIcon fontSize="small" />,
                disabled: route.isHome,
                onSelect: () => onAct('website.home_set', { link: page }),
              },
              {
                label: says('website.page_added'),
                icon: <AddIcon fontSize="small" />,
                detail: 'Underneath this one.',
                onSelect: () => onAddUnder(page),
              },
              {
                label: says('website.page_removed'),
                icon: <DeleteOutlineIcon fontSize="small" />,
                destructive: true,
                detail: 'Takes it off the site. The page itself is kept, and can be put back.',
                onSelect: () => onAct('website.page_removed', { link: page }),
              },
            ]}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

/**
 * A route that gives each record its own page, drawn as what the owner
 * edits: the list of records, one page each.
 *
 * It is a row in the same table rather than a note under its parent,
 * because its address is a real address and belongs under the other
 * addresses. What marks it as not-a-page is the turned arrow where every
 * other row has a grip — it cannot be reordered, and there is no page to
 * open, only the list.
 */
function GeneratedRow({
  route,
  tenant,
  site,
  listed,
  selected,
  onSelect,
}: {
  route: ResolvedRoute
  tenant: string
  site: string
  listed: ManifestType | undefined
  selected: boolean
  onSelect: (key: string) => void
}) {
  const prose = listed ? proseOf(listed, listed.name) : undefined
  return (
    <TableRow
      hover
      selected={selected}
      onClick={selectsRow(() => onSelect(selectionKey(route)))}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell sx={indented(route.depth)}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SubdirectoryArrowRightIcon fontSize="small" sx={{ width: 20, flexShrink: 0, color: 'text.disabled' }} />
          {listed && prose ? (
            <MuiLink component={RouterLink} to={routes.records(tenant, site, listed.name)} sx={{ fontWeight: 500 }}>
              A page for each {prose.title.toLowerCase()}
            </MuiLink>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              A page for each record
            </Typography>
          )}
        </Stack>
      </TableCell>
      <AddressCell path={route.urlPath} />
      <TableCell />
      <TableCell />
    </TableRow>
  )
}

/**
 * The selected row, edited beside the table.
 *
 * **The form is `PageSettings`, the same component the page's own
 * Settings face draws, and that reuse is the load-bearing decision.**
 * What it knows is the thing this panel would otherwise have to learn:
 * a page's title and search description belong to the Page, its slug,
 * menu name, menu description and home-ness belong to the Website, and
 * an author who thinks of all six as "this page" should not be asked to
 * care. One changed fact, one intent, to whichever component owns it.
 * Written twice, that rule would be right in one place and drift in the
 * other, and the place it drifted would be the one an owner uses daily.
 *
 * The save is not shared, and that is the one duplication here. It is
 * the same sequence the page's Settings face sends, named intent for
 * named intent, because the naming cannot be derived — a Website's
 * `route_updated` and `home_set` are the same payload shape, so "the act
 * intent" would get whichever the manifest listed first. Hoisting it
 * wants both callers moved at once and that is a change to a file this
 * work is not in. The wire is identical either way, which is what makes
 * the duplication survivable rather than merely convenient.
 *
 * **Three of the rows have no Page behind them, and each says what it
 * is rather than refusing to be clicked.** A row that highlights and
 * then shows nothing is a bug to the reader; a row that will not
 * highlight at all is a mystery, and the mystery is the expensive one,
 * because the questions an owner actually has about these rows — why
 * does this one not open, where do these pages come from — are exactly
 * the questions an inert row refuses to answer. So a generated route
 * explains that its pages come one per record and sends the reader to
 * the list that makes them; a menu heading explains that it groups and
 * opens nothing; a dangling route is named as the breakage it is. All
 * three offer the routing screen, which is where the things they *do*
 * have — a source, a pattern, a slug with no page — are edited.
 */
function PageInspector({
  tenant,
  site,
  website,
  manifest,
  selected,
  route,
  parent,
  listedAs,
  send,
  busy,
  onClear,
}: {
  tenant: string
  site: string
  website: Document
  manifest: Manifest | undefined
  /** What `?sel=` says, for the case where nothing answers to it. */
  selected: string
  /** The route that key resolves to, absent when nothing is selected. */
  route: ResolvedRoute | undefined
  parent: ResolvedRoute | undefined
  listedAs: (route: ResolvedRoute) => ManifestType | undefined
  send: ReturnType<typeof useIntent>['send']
  busy: boolean
  onClear: () => void
}) {
  const pageId = route?.targetId ?? ''
  const page = useGetAuthoringComponentQuery({ tenant, id: pageId }, { skip: !pageId })

  const save = async (next: PageSettingsValues, was: PageSettingsValues) => {
    if (!route || !pageId) return
    const seo = page.data ? pageFields(page.data).seo : undefined
    if (next.title !== was.title) {
      await send(pageId, 'page.retitled', { title: next.title })
    }
    // Both search fields in one send: `page.seo_set` carries the whole
    // `seo` object, so two would be the second discarding the first's
    // half of it.
    if (next.seoDescription !== was.seoDescription || next.seoKeywords !== was.seoKeywords) {
      await send(pageId, 'page.seo_set', {
        seo: { ...(seo ?? {}), description: next.seoDescription, keywords: next.seoKeywords },
      })
    }
    if (
      next.slug !== was.slug ||
      next.menuName !== was.menuName ||
      next.description !== was.description ||
      next.menus.join('\u0000') !== was.menus.join('\u0000')
    ) {
      await send(site, 'website.route_updated', {
        page: route.route.page,
        slug: next.slug,
        title: next.menuName,
        description: next.description,
        menus: next.menus,
      })
    }
    if (next.isHome && !was.isHome) {
      await send(site, 'website.home_set', { page: route.route.page })
    }
  }

  const listed = route?.generated ? listedAs(route) : undefined
  const prose = listed ? proseOf(listed, listed.name) : undefined
  // The page's own title heads the panel, falling back to the menu name
  // the table shows. The two differ exactly where the split this form
  // exists to hide is real — a page called "About us" linked in the menu
  // as "About" — and the heading names the thing the first field edits.
  const heading = !route
    ? 'Nothing selected'
    : route.generated
      ? prose
        ? `A page for each ${prose.title.toLowerCase()}`
        : 'A page for each record'
      : page.data
        ? pageFields(page.data).title || route.route.title || route.route.slug || 'Untitled page'
        : route.route.title || route.route.slug || 'Untitled page'

  return (
    <Paper sx={{ p: 2.5, position: { md: 'sticky' }, top: 24 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="h2" sx={{ flexGrow: 1 }}>
            {heading}
          </Typography>
          {/* The publication chip is the page's own reading, which the
              platform answers by path — so it is offered for a route
              that has one real address and withheld from a generated
              route, whose address is a pattern standing for ten. */}
          {route && !route.generated && route.targetId && (
            <PublicationStatus tenant={tenant} site={site} path={route.urlPath} page />
          )}
        </Stack>

        {!route ? (
          <Typography variant="caption" color="text.secondary">
            {selected
              ? 'That row is not on this site any more. Choose another one.'
              : 'Choose a page to change its name, its address and the menus it appears in, without leaving this list.'}
          </Typography>
        ) : route.generated ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              One page for every {prose ? prose.title.toLowerCase() : 'item'} in the list. There is nothing to write
              here: what these pages say comes from the items themselves, and how they are laid out comes from the
              pattern the agency set.
            </Typography>
            <AddressLine path={route.urlPath} />
            {listed && prose && (
              <Button variant="contained" component={RouterLink} to={routes.records(tenant, site, listed.name)}>
                Open {prose.plural.toLowerCase()}
              </Button>
            )}
            <RoutingLine tenant={tenant} site={site} what="which list these pages come from, and the pattern they use" />
          </Stack>
        ) : !route.route.page ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              A heading in the menu. It gathers the pages underneath it and opens nothing itself, so there is no page
              here to write.
            </Typography>
            <AddressLine path={route.urlPath} />
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {routeMenus(route.route).map((menu) => (
                <Chip key={menu} variant={menu === PRIMARY_MENU ? 'filled' : 'outlined'} label={menu} />
              ))}
            </Stack>
            <RoutingLine tenant={tenant} site={site} what="its name and where it sits" />
          </Stack>
        ) : route.dangling ? (
          <Stack spacing={2}>
            <Alert severity="error">
              The menu points at a page this site no longer holds, so this address answers with nothing. Nobody can
              reach it and nothing can be edited here.
            </Alert>
            <AddressLine path={route.urlPath} />
            <RoutingLine tenant={tenant} site={site} what="the route itself, to take it off the menu" />
          </Stack>
        ) : !route.targetId ? (
          // A route whose placement is inline rather than linked: the
          // page is defined inside the Website and has no stream of its
          // own, so there is no document for this form to load and no
          // page editor to open. The model allows it and the surface has
          // never made one; saying what it is beats an error about a
          // page that is not missing.
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              This page is built into the site, so it has no settings of its own to change.
            </Typography>
            <AddressLine path={route.urlPath} />
            <RoutingLine tenant={tenant} site={site} what="routes built this way" />
          </Stack>
        ) : page.isLoading ? (
          <Loading label="Opening the page…" />
        ) : page.error || !page.data ? (
          <QueryError error={page.error} title="Could not open this page" />
        ) : (
          <Stack spacing={2.5}>
            <PageSettings
              page={page.data}
              siteType={website.type}
              route={route}
              parent={parent}
              menuOptions={menusInUse(websiteFields(website).routing?.nodes)}
              manifest={manifest}
              busy={busy}
              onSave={(next, was) => save(next, was).catch(shown)}
            />
            {/* Under the form rather than over it: the panel's job is the
                edit, and the way in is what a reader reaches for once
                they find the edit is not the one they wanted. It is a
                link because it is somewhere to go. */}
            <Button
              fullWidth
              variant="outlined"
              component={RouterLink}
              to={routes.page(tenant, site, route.targetId ?? '')}
            >
              Open page editor
            </Button>
          </Stack>
        )}

        {route && (
          <Button size="small" onClick={onClear} sx={{ alignSelf: 'flex-start' }}>
            Clear selection
          </Button>
        )}
      </Stack>
    </Paper>
  )
}

/** An address, in the panel, drawn as the table draws one. */
function AddressLine({ path }: { path: string }) {
  return (
    <Typography
      variant="body2"
      sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem', color: 'text.secondary' }}
    >
      {path}
    </Typography>
  )
}

/**
 * The one sentence every unwritable row ends with.
 *
 * Each of the three has something real behind it that this screen
 * deliberately does not draw — a source collection, a pattern, a slug
 * with no page — and all three of them are edited in one place. Saying
 * which place, in the words of the thing the reader is looking at, is
 * the difference between a dead end and a detour.
 */
function RoutingLine({ tenant, site, what }: { tenant: string; site: string; what: string }) {
  return (
    <Typography variant="caption" color="text.secondary">
      The agency sets {what} on the{' '}
      <MuiLink component={RouterLink} to={routes.siteRouting(tenant, site)}>
        routing screen
      </MuiLink>
      .
    </Typography>
  )
}

/**
 * The lists this site's owner keeps: every record type the agency set up
 * to be listed, with how many there are.
 */
function Lists({
  tenant,
  site,
  manifest,
  website,
}: {
  tenant: string
  site: string
  manifest: Manifest | undefined
  website: Document
}) {
  const listed = listedTypes(manifest)
  if (listed.length === 0) return null
  return (
    <Paper sx={{ overflowX: 'auto' }}>
      <Stack sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h3">Lists</Typography>
        <Typography variant="caption" color="text.secondary">
          Content the site shows as a list, such as albums or team members. Each item is kept once and appears
          wherever the site uses it.
        </Typography>
      </Stack>
      {/* The same columns as the pages above, for the same reason and to
          the same widths where they mean the same thing: a list's
          address is a page address, and reading the two tables as one
          surface is most of what makes this screen a site rather than
          two features stacked. */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '34%' }}>List</TableCell>
            <TableCell sx={{ width: '28%' }}>Address</TableCell>
            <TableCell align="right" sx={{ width: '38%' }}>How many</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {listed.map((declared) => (
            <ListRow key={declared.name} tenant={tenant} site={site} declared={declared} website={website} />
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

function ListRow({
  tenant,
  site,
  declared,
  website,
}: {
  tenant: string
  site: string
  declared: ManifestType
  website: Document
}) {
  const collection = useGetAuthoringComponentQuery({ tenant, id: declared.collection ?? '' }, { skip: !declared.collection })
  const prose = proseOf(declared, declared.name)
  const count = members(collection.data).length
  const address = recordAddress(website, declared.collection)
  return (
    <TableRow>
      <TableCell>
        <Stack spacing={0.25}>
          <MuiLink component={RouterLink} to={routes.records(tenant, site, declared.name)} sx={{ fontWeight: 500 }}>
            {prose.plural}
          </MuiLink>
          {prose.description && (
            <Typography variant="caption" color="text.secondary">
              {prose.description}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <AddressCell path={address?.pattern ?? ''} />
      <TableCell align="right">
        <Chip variant="outlined" label={collection.isLoading ? '…' : `${count}`} />
      </TableCell>
    </TableRow>
  )
}
